import "./style.scss";

import { Modal } from "@mui/material";
import { getSignersDataObj } from "components/AgreementValidationModal/utils";
import ContractEditorLexical from "components/ContractsSettings/ContractEditorLexical";
import ModalBox from "components/Modal";
import type { UserVariables } from "components/shared/AgreementModal";
import DeleteModal from "components/shared/DeleteModal";
import MessageBlock from "components/shared/MessageBlock";
import MultiStep from "components/shared/MultiStep";
import { FileButton } from "components/shared/MultiStep/ImportFile";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import { useAuth } from "contexts/auth/AuthContext";
import { SmallBody } from "helpers/TypographyComponents";
import { getStatementFilename, translationsWithFallback } from "helpers/utils";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useSignatureProvider } from "hooks/useSignatureProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page } from "react-pdf";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CSSTransition } from "react-transition-group";
import { getUserListByCompany } from "store/adminPanelSlice";
import type { AgreementSigner, AgreementTemplate } from "store/agreementsStore";
import {
    AgreementSignatureStatus,
    AgreementStatus,
    approveAgreement,
    declineAgreement,
    deleteAgreement,
    fetchAgreement,
    fetchAgreementDocuments,
    fetchAgreementPDF,
    fetchAgreementPreview,
    fetchAgreementsComment,
    fetchAgreementDocoonSignatories,
    fetchAgreementTemplate,
    fetchAgreementValidators,
    forceSendAgreement,
    IAgFetchStatus,
    putAgreement,
    resetAgreementPDFStatus,
    revertAgreementToDraft,
    sendDocoonAgreement,
    setAgFetchStatus,
    updateAgFetchStatus,
} from "store/agreementsStore";
import { fetchArticle } from "store/articleStore";
import { fetchBeneficiar, updateBeneficiary } from "store/beneficiaryStore";
import { fetchChannelCompensationGroups, fetchChannels } from "store/channelStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchTerm } from "store/termStore";
import { fetchTermTemplate } from "store/termTemplatesStore";
import type { TFetchStatus, TPortalType } from "store/types";
import { EPortalType } from "store/types";

import DocoonSendOptionsModal from "components/shared/DocoonSignModal/DocoonSendOptionsModal";
import { AgreementPageStateProvider } from "./context/StateProvider";
import ContractValidationFooter from "./ContractValidationFooter";
import ContractValidationHeadPanel from "./ContractValidationHeadPanel";
import ContractValidationRightPanel from "./ContractValidationRightPanel";
import CrealoTeamModal from "./CrealoTeamModal";
import PublisherSignerWarning from "./PublisherSignerWarning";
import RevertToDraftModal from "./RevertToDraftModal";
import type { TBeneficiaryEmail } from "./SendAgreementValidationModal/VerifySignatoriesEmail";
import { VerifySignatoriesEmail } from "./SendAgreementValidationModal/VerifySignatoriesEmail";
import VerifyValidators from "./SendAgreementValidationModal/VerifyValidators";
import { agreementValidatorsDataTranform, useMissingVariables } from "./utils";
import VariablesWarningModal from "./VariablesWarningModal";

export type AgValidationModalType =
    | "revert-to-draft"
    | "delete-agreement"
    | "force-send-agreement"
    | "validators-signatories-summary"
    | "variables-warning"
    | "approve-and-send-agreement"
    | null;

const ContractValidationEdit = () => {
    // #region Variables
    const { id } = useParams();
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { portalType, getCompanySettings } = useCompanySettings();
    const usesValidationChain = !!getCompanySettings("uses_agreement_validation_chain");
    const { isAdminCrealo } = useAuth();
    const activeSignProvider = useActiveSignProvider();
    const { isDocoon } = useSignatureProvider(activeSignProvider.key);

    const { agreement, agreementValidators, agreementTemplate, agreementPDF, navigateAfterSendingAg } = useAppSelector(
        (state) => state.agreements,
    );
    const { agreementStatus, agreementPDFStatus, sendAgreementStatus } = useAppSelector(
        (state) => state.agreements.agFetchStatus,
    );

    const { userData } = useAppSelector((state) => state.settings);
    const { dataMap: usersMap } = useAppSelector((state) => state.adminPanel.users.company);

    const { term, termStatus } = useAppSelector((state) => state.term);
    const { article, articleStatus } = useAppSelector((state) => state.article);
    const { termTemplate, termTemplateStatus } = useAppSelector((state) => state.termTemplates);
    const { beneficiar: frameworkBeneficiar } = useAppSelector((state) => state.beneficiary);

    const isFrameworkContract = agreement?.relatedResource === "frameworkContract";

    // Related resource based on portal type — or on framework contract typology
    // (term_template) when the agreement targets a framework contract.
    const relatedResourceData = isFrameworkContract
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ({
              title: termTemplate?.name ?? "",
              titleId: undefined,
              beneficiarId: termTemplate?.beneficiaryId ?? agreement?.beneficiaryId ?? null,
              author: frameworkBeneficiar
                  ? `${frameworkBeneficiar.name ?? ""} ${frameworkBeneficiar.surname ?? ""}`.trim()
                  : "",
          } as any)
        : portalType === EPortalType.BOOK
          ? term
          : article;
    const relatedResourceStatus = isFrameworkContract
        ? termTemplateStatus
        : portalType === EPortalType.BOOK
          ? termStatus
          : articleStatus;

    const validators = agreementValidatorsDataTranform(usersMap, agreementValidators);

    const { channels, channelCompensationGroups } = useAppSelector((state) => state.channels);
    const [editorState, setEditorState] = useState({ stringifiedBlocks: "", html: "" });
    const [userVariables, setUserVariables] = useState<UserVariables>();
    // one loading status for 4 buttons
    const [isLoadingButton, setIsLoadingButton] = useState<"approve" | "decline" | "send-for-validation" | null>(null);
    const [activeStep, setActiveStep] = useState(0);
    const [editAgreementStatus, setEditAgreementStatus] = useState<TFetchStatus>("idle");
    const [numPages, setNumPages] = useState<number>();
    const [modalType, setModalType] = useState<AgValidationModalType>(null);
    const [showDocoonOptions, setShowDocoonOptions] = useState(false);
    const [docoonHasInformed, setDocoonHasInformed] = useState(false);

    const agreementPdfRef = useRef<HTMLDivElement>(null);

    const isOwner = agreement?.emittedBy == userData?.id;
    const owner = usersMap?.get(agreement?.emittedBy ?? -1);
    const isDraft = agreement?.status == AgreementStatus.DRAFT;
    const isNegotiating = agreement?.status == AgreementStatus.NEGOTIATING;
    const isNonDraftDocoon = agreement?.signProvider === "docoon" && !isDraft;

    const isLastValidator = agreementValidators[agreementValidators.length - 1]?.userId == userData?.id;
    const hasValidators = !!agreementValidators.length;
    const agreementBeneficiaries = (agreement?.signers as AgreementSigner[])?.filter(
        (signer) => signer.relatedResource === "beneficiary",
    ) as AgreementSigner[];
    // Count beneficiaries only: the internal signer is optional (never set with Docoon)
    const hasMultiSignature = agreementBeneficiaries?.length > 1;
    const agreementPublisherSigner = usersMap?.get(agreement?.publisherSignerId ?? -1);
    const hasAbilityToEditPublisherSigner = (isOwner || isAdminCrealo) && (isDraft || isNegotiating);

    const defaultBeneficiaryEmails = agreementBeneficiaries?.map((beneficiary) => ({
        id: beneficiary.relatedResourceId,
        email: beneficiary.email ?? "",
    })) as TBeneficiaryEmail[];

    const showAgreementPDF =
        agreement?.signedStatus === AgreementSignatureStatus.SIGNED ||
        agreement?.signedStatus === AgreementSignatureStatus.SENT;

    const isSigned = agreement?.signedStatus === AgreementSignatureStatus.SIGNED;

    const agreementPDFWidth = agreementPdfRef?.current?.offsetWidth;

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    // #region Logic
    useEffect(() => {
        dispatch(fetchAgreement({ agreementId: +(id as string) })).then((res) => {
            if (!res.payload) return;
            const ag = res.payload.data;
            if (ag) {
                if (ag.relatedResource === "frameworkContract") {
                    dispatch(fetchTermTemplate({ templateId: ag.relatedResourceId }));
                    if (ag.beneficiaryId) dispatch(fetchBeneficiar(ag.beneficiaryId));
                } else if (portalType === EPortalType.BOOK) {
                    dispatch(fetchTerm(ag.relatedResourceId));
                } else {
                    dispatch(fetchArticle({ titleId: ag.titleId, articleId: ag.relatedResourceId }));
                }
                setUserVariables(ag.userVariables);
                if (ag.templateId) dispatch(fetchAgreementTemplate(+ag.templateId));
                const shouldShowPDF = [AgreementSignatureStatus.SIGNED, AgreementSignatureStatus.SENT].includes(
                    ag.signedStatus,
                );
                if (shouldShowPDF) {
                    dispatch(fetchAgreementPDF({ agreementId: ag.id }));
                }
            }
        });
        dispatch(fetchAgreementValidators({ agreementId: +id! }));
        dispatch(fetchAgreementsComment({ agreementId: +id! }));
        dispatch(fetchAgreementDocuments({ agreementId: +id! }));
        dispatch(getUserListByCompany());
        if (!channels.length) dispatch(fetchChannels());
        if (!channelCompensationGroups.length) dispatch(fetchChannelCompensationGroups());
    }, []);

    const handleStep = {
        change: (_: any, idx: number) => setActiveStep(idx),
        next: () => setActiveStep((prev) => prev + 1),
        previous: () => setActiveStep((prev) => prev - 1),
    };
    /**
     * Handle save agreement :
     * _ by press the button save
     * _ when downloading PDF
     * @param sendToast true if it should send a toast
     */
    const handleSaveAgreement = async (sendToast?: boolean) => {
        sendToast = sendToast ?? true;
        // custom loading state, not in store because 2 same fetch for handleVariableChange function
        setEditAgreementStatus("pending");
        const newTemplate = {
            blocks: editorState.stringifiedBlocks,
            html: editorState.html,
            userVariables: userVariables,
        };
        const response = await dispatch(putAgreement({ data: newTemplate, id: agreement?.id as number }));
        if (response.meta.requestStatus === "fulfilled") {
            sendToast && toast.success(`${t("ContractSettings.EditAgreementSuccess")}`);
            setEditAgreementStatus("succeeded");
        }
        if (response.meta.requestStatus === "rejected") {
            setEditAgreementStatus("failed");
        }
    };

    const handleVariableChange = (variables?: UserVariables) => {
        setUserVariables(variables);
        const newContract = {
            blocks: editorState.stringifiedBlocks,
            html: editorState.html,
            userVariables: variables,
        };
        dispatch(putAgreement({ data: newContract, id: agreement?.id ?? -1 }));
    };

    const { missingVariables, missingVariablesByBeneficiary, beneficiariesMissingVariables } = useMissingVariables(
        isDraft,
        userVariables,
        agreement,
    );

    const handleDocoonSend = async (options?: {
        signatureTemplate?: string;
        signSequential: number;
        serviceEdc?: string;
        typeDocEdc?: string;
        comment?: string;
    }) => {
        setIsLoadingButton("send-for-validation");
        try {
            const signatoriesRes = await dispatch(fetchAgreementDocoonSignatories({ agreementId: +(id ?? -1) }));
            if (signatoriesRes.meta.requestStatus !== "fulfilled") return;

            const signatairesIds = (signatoriesRes.payload as { docoonId: number }[]).map((s) => s.docoonId).join(",");

            await dispatch(
                sendDocoonAgreement({
                    agreementId: +(id ?? -1),
                    signatairesIds,
                    signatureTemplate: options?.signatureTemplate,
                    signSequential: options?.signSequential,
                    serviceEdc: options?.serviceEdc,
                    typeDocEdc: options?.typeDocEdc,
                    comment: options?.comment,
                }),
            );
            dispatch(fetchAgreement({ agreementId: +(id ?? -1) })).then((res) => {
                if (!res.payload) return;
                const updatedAgreement = res.payload.data;
                if (updatedAgreement?.templateId) {
                    dispatch(fetchAgreementTemplate(+updatedAgreement.templateId));
                }
                if (
                    [AgreementSignatureStatus.SIGNED, AgreementSignatureStatus.SENT].includes(
                        updatedAgreement?.signedStatus,
                    )
                ) {
                    dispatch(fetchAgreementPDF({ agreementId: +(id ?? -1) }));
                }
            });
        } finally {
            setIsLoadingButton(null);
        }
    };

    const handleValidate = (status: AgreementStatus | "approve" | "decline") => {
        if (missingVariables?.length || beneficiariesMissingVariables?.length) {
            setModalType("variables-warning");
            return;
        }
        if (status == "approve") {
            setIsLoadingButton("approve");
            dispatch(approveAgreement({ agreementId: +(id ?? -1) })).then(() => {
                navigate("/agreements");
            });
            return;
        }
        if (status == "decline") {
            setIsLoadingButton("decline");
            dispatch(declineAgreement({ agreementId: +(id ?? -1) })).then(() => {
                navigate("/agreements");
            });
            return;
        }
        // Docoon send options: only collected at send-time when the chain setting is OFF.
        // When the chain is ON, options were persisted at creation (regardless of the
        // template having validators or not) — skip the modal.
        if (isDocoon && !usesValidationChain) {
            // Signatories are edited in the right panel: re-read them to know if sequential mode is forced
            dispatch(fetchAgreementDocoonSignatories({ agreementId: +(id ?? -1) })).then((res) => {
                // On a failed read the backend still forces sequential mode at send time
                setDocoonHasInformed(
                    fetchAgreementDocoonSignatories.fulfilled.match(res) && res.payload.some((s) => !!s.isInformed),
                );
                setShowDocoonOptions(true);
            });
            return;
        }
        if (isDocoon && !hasValidators) {
            handleDocoonSend();
            return;
        }
        setModalType("validators-signatories-summary");
    };

    const handleOnDelete = async () => {
        await dispatch(deleteAgreement({ agreementId: +(id ?? -1) })).then(() => {
            navigate("/agreements");
        });
    };

    const handleFileDownload = async () => {
        const filename = getStatementFilename(
            relatedResourceData?.author ?? "",
            relatedResourceData?.title ?? "",
            t("Summary.Contract"),
            new Date(),
        );
        dispatch(
            fetchAgreementPreview({
                filename,
                agreementId: +(id ?? -1),
            }),
        );
    };

    const onModalSend = async (beneficiaryEmails: TBeneficiaryEmail[]) => {
        setIsLoadingButton("send-for-validation");
        try {
            let hasChangedEmail = false;
            const emailsData = beneficiaryEmails.map(async (beneficiary, index) => {
                hasChangedEmail = defaultBeneficiaryEmails[index].email !== beneficiary.email;
                if (hasChangedEmail) {
                    return dispatch(
                        updateBeneficiary({
                            id: beneficiary.id,
                            data: { email: beneficiary.email },
                        }),
                    );
                }
            });
            const updateResponse = await Promise.all(emailsData);
            const isUpdateFulfilled = updateResponse.every((req: any) => req?.meta?.requestStatus === "fulfilled");

            if (isUpdateFulfilled || !hasChangedEmail) {
                const newContract = {
                    status: AgreementStatus.NEGOTIATING,
                    blocks: editorState.stringifiedBlocks,
                    html: editorState.html,
                    userVariables: userVariables ?? {},
                };
                await dispatch(
                    putAgreement({
                        data: newContract,
                        id: agreement?.id ?? -1,
                        behavior: "send_for_signature_or_validation",
                    }),
                );
            }
        } finally {
            setIsLoadingButton(null);
        }
    };

    // navigate() doesn't work after dispatch(putAgreement())
    useEffect(() => {
        if (navigateAfterSendingAg) {
            navigate("/agreements");
        }
    }, [navigateAfterSendingAg]);

    const onSignatoryModalClose = () => {
        setModalType(null);
        setIsLoadingButton(null);
    };
    const beneficiaryIds = (() => {
        const signers = (agreement?.signers as AgreementSigner[]) ?? [];
        const publisherSigner = signers.find((s) => s.relatedResource === "user");
        const beneficiarySigs = signers
            .filter((s) => s.relatedResource === "beneficiary")
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return publisherSigner
            ? [publisherSigner.relatedResourceId, ...beneficiarySigs.map((s) => s.relatedResourceId)]
            : beneficiarySigs.map((s) => s.relatedResourceId);
    })();

    const titleBeneficiaries = useMemo(() => {
        if (!agreement) return;
        return { options: beneficiaryIds, beneficiary: getSignersDataObj(agreement.signers as AgreementSigner[]) };
    }, [agreement]);

    if (isNaN(+(id ?? NaN)) || id == undefined) return null;

    const showCrealoTeamActions = isAdminCrealo && agreement?.status !== AgreementStatus.SENT;

    const handleExportPDF = async () => {
        const { header, agreementTemplate, ...rest } = agreement?.userVariables as any;
        if (isOwner) await handleSaveAgreement(false);
        dispatch(
            fetchAgreementPreview({
                agreementId: +(id ?? -1),
                filename: getStatementFilename(
                    relatedResourceData?.author ?? "",
                    relatedResourceData?.title ?? "",
                    t("Summary.Contract"),
                    new Date(),
                ),
            }),
        );
    };

    const ThreeDotMenuOptions: ThreeDotMenuOptions[] = [
        {
            id: 1,
            name: t("ContractValidationEdit.ExportPDF"),
            action: () => handleExportPDF(),
        },
        {
            id: 2,
            name: t("ContractValidationEdit.RevertToDraft"),
            action: () => setModalType("revert-to-draft"),
            hide: isDraft || !isOwner || isSigned,
        },
        {
            id: 3,
            name: t(
                agreement?.type === "avenant"
                    ? "ContractValidationEdit.DeleteAvenant"
                    : "ContractValidationEdit.Delete",
            ),
            action: () => setModalType("delete-agreement"),
            hide: !isDraft || !isOwner || isNonDraftDocoon,
        },
        {
            id: 4,
            name: "[Crealo Team] Actions",
            action: () => setModalType("force-send-agreement"),
            hide: !showCrealoTeamActions,
        },
    ];

    const handleReturnToAgreements = () => {
        navigate("/agreements");
        dispatch(resetAgreementPDFStatus());
    };

    // #region Render
    return (
        <AgreementPageStateProvider
            agreement={agreement}
            agreementPublisherSigner={agreementPublisherSigner}
            isOwner={isOwner}
            isDraft={isDraft}
            isSigned={isSigned}
            hasAbilityToEditPublisherSigner={hasAbilityToEditPublisherSigner}
        >
            {/**
             * Crealo Team send agreement for signature modal
             */}
            {showCrealoTeamActions && <CrealoTeamModal modalType={modalType} setModalType={setModalType} />}
            <DocoonSendOptionsModal
                open={showDocoonOptions}
                isLoading={isLoadingButton === "send-for-validation"}
                forceSequential={docoonHasInformed}
                onClose={() => setShowDocoonOptions(false)}
                onConfirm={(options) => {
                    setShowDocoonOptions(false);
                    handleDocoonSend(options);
                }}
            />

            <RevertToDraftModal
                open={modalType === "revert-to-draft"}
                onClose={() => setModalType(null)}
                setUserVariables={setUserVariables}
            />

            <div className="contract-validation-edit">
                <div className="contract-validation-edit-left-panel">
                    <div className="contract-validation-edit-left-panel-wrapper">
                        <ContractValidationHeadPanel
                            handleReturnToAgreements={handleReturnToAgreements}
                            owner={owner}
                            relatedResourceStatus={relatedResourceStatus}
                            relatedResourceData={relatedResourceData}
                            agreementTemplate={agreementTemplate}
                            handleSaveAgreement={handleSaveAgreement}
                            ThreeDotMenuOptions={ThreeDotMenuOptions}
                            isSaving={editAgreementStatus === "pending"}
                            portalType={portalType as TPortalType}
                        />
                        <div className="contract-validation-editor-lexical and-validation">
                            {isDraft && !isOwner && relatedResourceStatus === "succeeded" && (
                                <div style={{ display: "flex" }}>
                                    <SmallBody color="info">{t("ContractValidationEdit.OnlyCreatorCanEdit")}</SmallBody>
                                </div>
                            )}
                            {agreementStatus == "succeeded" &&
                                !agreementPublisherSigner &&
                                !isDocoon &&
                                !isSigned &&
                                agreement?.signProvider !== "docoon" && <PublisherSignerWarning />}
                            {agreementStatus == "succeeded" && agreement?.blocks && !showAgreementPDF && (
                                <ContractEditorLexical
                                    agreement={
                                        {
                                            blocks:
                                                typeof agreement.blocks === "string"
                                                    ? JSON.parse(agreement.blocks)
                                                    : agreement.blocks,
                                            // Pass relatedResource so the editor's isFrameworkContract check
                                            // resolves correctly — otherwise framework-only variables
                                            // ({{startDate}}, {{folioPrice}}, …) fall back to raw text in
                                            // PanelVariables (which reads agreementVariablesList).
                                            relatedResource: agreement.relatedResource,
                                        } as unknown as AgreementTemplate
                                    }
                                    userVariables={userVariables}
                                    agreementUserVariables={agreement.userVariables}
                                    isAgreementEdit
                                    isReadOnly={!isOwner || agreement?.status !== AgreementStatus.DRAFT}
                                    onSetupSave={(data: any) => setEditorState(data)}
                                    key={agreement.id}
                                    hasMultiSignature={hasMultiSignature}
                                    beneficiariesFormData={{ beneficiaryIds, titleBeneficiaries }}
                                />
                            )}
                            <CSSTransition
                                nodeRef={agreementPdfRef}
                                in={agreementPDFStatus == "succeeded" && showAgreementPDF && !!agreementPDF}
                                timeout={200}
                                classNames="agreement-pdf-preview"
                                unmountOnExit
                            >
                                <div className="agreement-pdf-wrapper" ref={agreementPdfRef}>
                                    <Document
                                        file={`data:application/pdf;base64,${agreementPDF}`}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        className="agreement-pdf"
                                        loading={
                                            <div className="agreement-pdf-loading">
                                                <Spinner />
                                            </div>
                                        }
                                    >
                                        {Array.apply(null, Array(numPages))
                                            .map((x, i) => i + 1)
                                            .map((page) => (
                                                <Page
                                                    renderAnnotationLayer={false}
                                                    pageNumber={page}
                                                    width={agreementPDFWidth}
                                                    key={`page${page}o${numPages}`}
                                                />
                                            ))}
                                    </Document>
                                </div>
                            </CSSTransition>
                        </div>
                    </div>
                    {/**
                     * Show footer only if there is a publisher signer
                     */}
                    {!modalType && (!!agreementPublisherSigner || isDocoon) && (
                        <ContractValidationFooter
                            agreementStatus={agreementStatus}
                            isLoadingButton={isLoadingButton}
                            handleValidate={handleValidate}
                            hasValidators={hasValidators}
                            agreementValidators={agreementValidators}
                            userData={userData}
                            isLastValidator={isLastValidator}
                            handleShowApproveAndSendModal={() => setModalType("approve-and-send-agreement")}
                        />
                    )}
                </div>
                <ContractValidationRightPanel
                    relatedResourceStatus={relatedResourceStatus}
                    hasValidators={hasValidators}
                    validators={validators}
                    agreementStatus={agreementStatus}
                    owner={owner}
                    handleVariableChange={handleVariableChange}
                    userVariables={userVariables}
                    setUserVariables={setUserVariables}
                    hasMultiSignature={hasMultiSignature}
                    missingVariables={missingVariables}
                    channels={channels}
                    beneficiariesMissingVariables={beneficiariesMissingVariables}
                />

                {/** MODALS */}
                <VariablesWarningModal
                    open={modalType === "variables-warning"}
                    onClose={() => setModalType(null)}
                    missingVariables={missingVariables}
                    hasMultiSignature={hasMultiSignature}
                    missingVariablesByBeneficiary={missingVariablesByBeneficiary}
                />
                <Modal open={modalType === "validators-signatories-summary"} onClose={onSignatoryModalClose}>
                    <MultiStep
                        id="form"
                        activeStep={activeStep}
                        onStepChange={handleStep.change}
                        title={t("Agreements.SendForValidation")}
                        onClose={onSignatoryModalClose}
                        animatedStepper
                        steps={[
                            {
                                title: t("Agreements.Approvers"),
                                component: (
                                    <VerifyValidators
                                        validators={validators}
                                        handleNextStep={handleStep.next}
                                        onClose={onSignatoryModalClose}
                                        isLastStep={isDocoon}
                                        onConfirm={() => onModalSend(defaultBeneficiaryEmails)}
                                        isConfirming={isLoadingButton === "send-for-validation"}
                                    />
                                ),
                                hidden: !validators?.length,
                            },
                            {
                                title: t("ContractValidation.Signatories"),
                                component: (
                                    <VerifySignatoriesEmail
                                        relatedResourceData={relatedResourceData}
                                        agreementId={agreement?.id ?? -1}
                                        onSend={(beneficiaryEmails) => onModalSend(beneficiaryEmails)}
                                        publisherSigner={agreementPublisherSigner}
                                        isLoadingButton={isLoadingButton}
                                        defaultBeneficiaryEmails={defaultBeneficiaryEmails}
                                        beneficiaries={agreementBeneficiaries}
                                        hasValidators={hasValidators}
                                        handlePreviousStep={handleStep.previous}
                                    />
                                ),
                                hidden: isDocoon,
                            },
                        ]}
                    />
                </Modal>

                {/** Validate contract modal (for validators) */}
                <ModalBox
                    open={modalType === "approve-and-send-agreement"}
                    mainTitle={t("ContractValidationEdit.ValidateModalTitle")}
                    subTitle={`${relatedResourceData?.author} ${t("ContractValidationEdit.ValidateModalSubtitle")}`}
                    style={{ maxWidth: "560px" }}
                    onClose={() => setModalType(null)}
                >
                    <>
                        <FileButton
                            label={t("ContractSettings.DownloadPreviewFile")}
                            onClick={() => handleFileDownload()}
                            style={{ width: "100%" }}
                        />
                        <div style={{ display: "flex", gap: "16px", marginTop: "24px" }}>
                            <StyledButton buttonType="secondary" onClick={() => setModalType(null)} fullWidth>
                                {t("Cancel")}
                            </StyledButton>
                            <StyledButton
                                buttonType="primary"
                                loading={isLoadingButton === "approve"}
                                onClick={() => handleValidate("approve")}
                                fullWidth
                            >
                                {t("ContractValidationEdit.ApproveAndSend")}
                            </StyledButton>
                        </div>
                    </>
                </ModalBox>

                <DeleteModal
                    open={modalType === "delete-agreement"}
                    onClose={() => setModalType(null)}
                    elementTitle={t(
                        agreement?.type === "avenant"
                            ? "ContractValidationEdit.DeleteModalTitleAvenant"
                            : "ContractValidationEdit.DeleteModalTitleContract",
                    )}
                    onDelete={handleOnDelete}
                />
            </div>
        </AgreementPageStateProvider>
    );
};

export default ContractValidationEdit;
